import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';
import '../../component-styles/RecordPages.css';
import CreditFolioModal from '../../components/Credits/CreditFolioModal';
import NewCreditModal from '../../components/Credits/NewCreditModal';
import RefundCreditModal from '../../components/Credits/RefundCreditModal';
import API_BASE from '../../utils/api.js';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

export default function CreditsSection() {
  const { projectId } = useParams();

  const { permissions } = usePermissions();
  const canCreate = permissions.createApplyCreditsRetainers !== false;

  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFolio, setShowFolio] = useState(null);
  const [showNewCredit, setShowNewCredit] = useState(false);
  const [refundCredit, setRefundCredit] = useState(null);


  /* ----------------------------
     LOAD CREDITS
  ----------------------------- */
  useEffect(() => {
    if (!projectId) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/credits/${projectId}`, {
          headers: { userid: localStorage.getItem('userId') },
        });

        const data = await res.json();
        const list = Array.isArray(data) ? data : data.Items || [];
        setCredits(list);
      } catch (err) {
        console.error('Error loading credits:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  /* ----------------------------
     TOTALS
  ----------------------------- */
  const totals = useMemo(() => ({
    available: credits.reduce((s, c) => s + (c.availableBalance || 0), 0),
    total: credits.reduce((s, c) => s + (c.total || 0), 0),
    used: credits.reduce((s, c) => s + ((c.total || 0) - (c.availableBalance || 0)), 0),
  }), [credits]);

  const handleCreditCreated = (newCredit) => {
    setCredits((prev) => [...prev, newCredit]);
  };

  return (
    <section className="record-section">
      {/* Header */}
      <div className="record-top-row">
        <div className="record-header-bar">
          <h2 className="record-title">Credits</h2>
          
        </div>
      <p className="record-subtext">
          These credits are automatically created when a client overpays a retainer,
          invoice, proposal, or purchase order.
        </p>
  

        {canCreate && (
          <button
            className="record-add-btn"
            onClick={() => setShowNewCredit(true)}
          >
            + New Credit
          </button>
        )}
      </div>

      {/* Metrics + Table */}
      <div className="record-card">
        <div className="record-metrics">
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.available)}</div>
            <div className="record-metric-label">Available Credits</div>
          </div>

          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.total)}</div>
            <div className="record-metric-label">Total Credits</div>
          </div>

          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.used)}</div>
            <div className="record-metric-label">Used Credits</div>
          </div>
        </div>

        <div className="record-table-wrap">
          {loading ? (
            <div className="record-empty">Loading credits…</div>
          ) : credits.length === 0 ? (
            <div className="record-empty">No credits yet.</div>
          ) : (
            <table className="record-table credits-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Credit #</th>
                  <th>Folio</th>
                  <th>Description</th>
                  <th>Total</th>
                  <th>Available Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {credits.map((c) => (
                  <tr key={c.creditId}>
                    <td>{c.date?.slice(0, 10) || '—'}</td>
                    <td>{c.creditNumber}</td>

                    <td className="folio-cell">
                      <div
                        className="folio-item folio-click"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFolio(c);
                        }}
                      >
                        <img
                          src="/folder-icon.png"
                          alt="Folder"
                          className="folio-folder-img"
                        />
                      </div>
                    </td>

                    <td className="desc-cell">{c.description}</td>
                    <td>{formatCurrency(c.total)}</td>
                    <td>{formatCurrency(c.availableBalance)}</td>

                    <td>
                      {c.availableBalance > 0 ? (
                        <button
                          className="link-btn"
                          onClick={() => setRefundCredit(c)}
                        >
                          Refund
                        </button>
                      ) : (
                        <span className="no-refund">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showFolio && (
        <CreditFolioModal
          credit={showFolio}
          projectId={projectId}
          onClose={() => setShowFolio(null)}
        />
      )}

      {showNewCredit && (
        <NewCreditModal
          projectId={projectId}
          onClose={() => setShowNewCredit(false)}
          onCreated={handleCreditCreated}
        />
      )}

      {refundCredit && (
        <RefundCreditModal
          credit={refundCredit}
          projectId={projectId}
          onClose={() => setRefundCredit(null)}
          onRefunded={(updated) =>
            setCredits((prev) =>
              prev.map((c) =>
                c.creditId === updated.creditId ? updated : c
              )
            )
          }
        />
      )}
    </section>
  );
}
